import { AnyExtension, Extensions } from '@tiptap/core';
import Bold from '@tiptap/extension-bold';
import Code from '@tiptap/extension-code';
import HardBreak from '@tiptap/extension-hard-break';
import Italic from '@tiptap/extension-italic';
import Link from '@tiptap/extension-link';
import ListKeymap from '@tiptap/extension-list-keymap';
import Placeholder from '@tiptap/extension-placeholder';
import Strike from '@tiptap/extension-strike';
import Underline from '@tiptap/extension-underline';
import StarterKit from '@tiptap/starter-kit';
import { Markdown } from 'tiptap-markdown';

import { ClearMarksOnEnter } from './custom-extensions/ClearMarksOnEnter';

/**
 * Override the default hard break behavior to allow for
 * the shift + enter key combination to create a new line.
 */
export const CustomHardBreak = HardBreak.extend({
  addKeyboardShortcuts() {
    return {
      'Shift-Enter': () => this.editor.commands.enter(),
    };
  },
});

function escapeMarkdownLinkComponent(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/[()"]/g, '\\$&');
}

/**
 * @tiptap/extension-underline has no built-in markdown spec, so
 * tiptap-markdown silently strips underline marks during serialization.
 * We add ++/++ markers here so underlined text round-trips correctly;
 * the rendering side already parses ++text++ via remark-ins in useMessageComponents.
 */
const ExitableBold = Bold.extend({ exitable: true });
const ExitableCode = Code.extend({ exitable: true });
const ExitableItalic = Italic.extend({ exitable: true });
const ExitableStrike = Strike.extend({ exitable: true });

const MarkdownUnderline = Underline.extend({
  exitable: true,
  addStorage() {
    return {
      markdown: {
        serialize: {
          open: '++',
          close: '++',
          expelEnclosingWhitespace: true,
        },
        parse: {
          // handled by markdown-it
        },
      },
    };
  },
});

/**
 * Always use [text](url) format instead of the default <url>
 * autolink format which renders raw angle brackets in chat.
 */
const MarkdownLink = Link.extend({
  exitable: true,
  addStorage() {
    return {
      markdown: {
        serialize: {
          open: '[',
          close(
            _state: unknown,
            mark: { attrs: { href: string; title?: string } },
          ) {
            const href = escapeMarkdownLinkComponent(mark.attrs.href);
            const title = mark.attrs.title
              ? ` "${escapeMarkdownLinkComponent(mark.attrs.title)}"`
              : '';
            return `](${href}${title})`;
          },
          mixable: true,
        },
        parse: {
          // handled by markdown-it
        },
      },
    };
  },
}).configure({
  openOnClick: false,
});

interface MessageEditorExtensionsOptions {
  placeholder?: string;
  additionalExtensions?: AnyExtension[];
  codeHTMLAttributes?: Record<string, string>;
  boldHTMLAttributes?: Record<string, string>;
  headingLevels?: (1 | 2 | 3)[];
  underline?: boolean;
}

/**
 * Returns the base set of tiptap extensions used for message-style editors
 * (chat input, welcome message settings, etc.).
 *
 * Pass chat-specific extensions (SuggestionCommand, etc.)
 * via `additionalExtensions`.
 */
export function getMessageEditorExtensions(
  options?: MessageEditorExtensionsOptions,
): Extensions {
  const starterKit = StarterKit.configure({
    heading: {
      levels: options?.headingLevels ?? [2, 3],
    },
    bulletList: {
      HTMLAttributes: {
        class: 'list-disc list-outside pl-5 mt-0.5 mb-2 ml-2.5',
      },
    },
    orderedList: {
      HTMLAttributes: {
        class: 'list-decimal list-outside pl-5 mt-0.5 mb-2 ml-2.5',
      },
    },
    dropcursor: false,
    bold: false,
    code: false,
    italic: false,
    strike: false,
  }) as AnyExtension;

  return [
    starterKit,
    options?.boldHTMLAttributes
      ? ExitableBold.configure({ HTMLAttributes: options.boldHTMLAttributes })
      : ExitableBold,
    options?.codeHTMLAttributes
      ? ExitableCode.configure({ HTMLAttributes: options.codeHTMLAttributes })
      : ExitableCode,
    ExitableItalic,
    ExitableStrike,
    Markdown,
    ClearMarksOnEnter,
    Placeholder.configure({
      emptyEditorClass:
        'first:before:content-[attr(data-placeholder)] first:before:float-left first:before:text-[#9B9FA3] first:before:h-0 first:before:pointer-events-none',
      placeholder: options?.placeholder ?? '',
    }),
    ...(options?.underline === false ? [] : [MarkdownUnderline]),
    MarkdownLink,
    CustomHardBreak,
    ListKeymap,
    ...(options?.additionalExtensions ?? []),
  ];
}
